import React, { useState } from 'react';
import { CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Formik } from 'formik';

import { InfoAlert } from '@magoosh/lib/alerts';
import { DefaultFilledButton, PrimaryButton, ResponsiveButtonGroup } from '@magoosh/lib/button';
import { Form } from '@magoosh/lib/forms';
import { PaymentDetails } from 'types/payment_details';
import { Payment, PurchaseAgreement } from 'types/purchase_agreement';
import { bugsnagNotify } from 'utilities/bugsnag';
import { formatPrice } from 'utilities/money';
import { pluralize } from 'utilities/pluralize';

import * as paymentsApi from '@payments/api';
import StripeCardInputs from '@payments/components/inputs/stripe_card';
import { PaymentError } from '@payments/errors';

import * as api from '@test_prep/app/api';

import PaymentMethodSummary from '../payment_method_summary';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  paymentMethod: PaymentDetails | null;
  purchaseAgreement: PurchaseAgreement;
  stripeToken: string;
}

interface CardFormValues {
  cardName: string;
}

const INITIAL_VALUES: CardFormValues = { cardName: '' };

const GENERIC_ERROR = 'We couldn\'t update your payment method. Please try again and if you are still having trouble, contact us at <a class="text-danger" href="mailto:help@magoosh.com">help@magoosh.com</a>.';

const EditPaymentMethod: React.FC<Props> = (props) => {
  const elements = useElements();
  const stripe = useStripe();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // What the old card still owes, which saving a new card charges it for straight away. Only an
  // agreement collects like this: a legacy subscription's stand-in has no id, and its card retries
  // nothing until Stripe next tries (see Payments::UpdatePaymentMethodService).
  const chargesFailedPayments = !!props.purchaseAgreement.id;
  const failedPayments = chargesFailedPayments
    ? props.purchaseAgreement.payments.filter((payment: Payment) => (
      payment.status === 'failed' || payment.status === 'incomplete'
    ))
    : [];
  const failedTotal = failedPayments.reduce((total, payment) => total + payment.total, 0);

  const planDescription = props.purchaseAgreement.id
    ? `purchase agreement ${props.purchaseAgreement.id}`
    : `subscription ${props.purchaseAgreement.legacySubscriptionId}`;

  const onSubmit = async (values: CardFormValues, { setErrors }) => {
    setIsSubmitting(true);

    try {
      const card = elements.getElement(CardNumberElement);
      const { stripePaymentMethod } = await paymentsApi.createStripePaymentMethod(
        stripe,
        card,
        values
      );

      let response = await api.updatePurchaseAgreementPaymentMethod(
        props.purchaseAgreement,
        stripePaymentMethod.id
      );

      // A bank that wants the card authenticated hands back a challenge for the student to
      // answer here, after which the same request goes again carrying what they answered
      if (response.actionRequired?.type === 'confirm_setup') {
        const confirmation = await stripe.confirmCardSetup(response.actionRequired.clientSecret);

        if (confirmation.error) {
          throw new PaymentError(
            GENERIC_ERROR,
            `Error confirming a card setup for ${planDescription}: ${JSON.stringify(confirmation.error)}`
          );
        }

        response = await api.updatePurchaseAgreementPaymentMethod(
          props.purchaseAgreement,
          stripePaymentMethod.id,
          confirmation.setupIntent.id
        );
      }

      if (!response.paymentMethod) {
        throw new PaymentError(
          GENERIC_ERROR,
          `Card was never saved for ${planDescription}: ${JSON.stringify(response)}`
        );
      }

      props.onSuccess();
    } catch (exception) {
      setErrors({ ...exception.errors, _error: exception.errors?._error || GENERIC_ERROR } as any);
      bugsnagNotify(exception.debug || exception);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-m">
      <div className="align-items-center d-flex gap-xs">
        <div><strong>Current:</strong></div>

        <PaymentMethodSummary paymentMethod={props.paymentMethod} />
      </div>

      {
        failedPayments.length > 0 && (
          <InfoAlert className="small">
            {pluralize(failedPayments.length, 'payment')} totalling{' '}
            {formatPrice(failedTotal)} did not go through on the card we have. We will charge the
            card you enter below for {failedPayments.length === 1 ? 'it' : 'them'}.
          </InfoAlert>
        )
      }

      <Formik initialValues={INITIAL_VALUES} onSubmit={onSubmit}>
        {({ handleSubmit }) => (
          <Form>
            <StripeCardInputs />

            <div className="u-margin-T-m">
              <ResponsiveButtonGroup>
                <PrimaryButton onClick={handleSubmit} submitting={isSubmitting} type="submit">
                  Save Card
                </PrimaryButton>

                <DefaultFilledButton disabled={isSubmitting} onClick={props.onClose}>
                  Cancel
                </DefaultFilledButton>
              </ResponsiveButtonGroup>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

// Stripe's card fields only work inside Elements, which needs the publishable key
const WrappedEditPaymentMethod: React.FC<Props> = (props) => (
  <Elements stripe={loadStripe(props.stripeToken)}>
    <EditPaymentMethod {...props} />
  </Elements>
);

export default WrappedEditPaymentMethod;
